import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Button, ModalBody, ModalHeader } from 'reactstrap';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import useMediaQuery from '../../../utils/hooks/mediaQuery.hook';
import FacebookShareButton from '../../../components/FacebookShareButton';
import SwipeModal from '../../../components/SwipeModal';
import ScoreboardModalSkeleton from './skeletons/ScoreboardModalSkeleton';
import { OrderContext } from '../../../context/OrderContext';
import ToastSuccessMessage from '../../../components/ToastSuccessMessage';
import { CampaignContext } from '../../../context/CampaignWrapper';

import { ReactComponent as TrophyIcon } from '../../../assets/icons/trophy.svg';
import { ReactComponent as BasketIcon } from '../../../assets/icons/basket.svg';
import { ScoreboardQuery, useScoreboardQuery } from '../../../generated/graphql';
import { isDefined } from '../../../utils/helpers';

interface IScoreboardModal {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  onClose: () => void
}

const ScoreboardModal = ({ isOpen, setIsOpen, onClose } : IScoreboardModal) => {
  const { slug } = useParams();
  const { isMobile } = useMediaQuery();
  const [translate] = useTranslation();
  const { campaign: { textConfig, winnerCount } } = useContext(CampaignContext);
  const { setPurchaserInfo } = useContext(OrderContext);
  const { data, loading } = useScoreboardQuery({
    skip: !slug,
    variables: {
      slug: slug || '',
    },
  });

  const { scoreboard = [] } = data || {};

  const modalStyle = isMobile ? 'swipe-modal' : 'scoreboard';

  const isScoreboardEmpty = !scoreboard?.length;
  const scoreboardMessage = textConfig?.scoreboard;

  if (loading) return <ScoreboardModalSkeleton />;

  const toggleOpen = () => {
    setIsOpen((state) => {
      if (state) onClose();
      return !state;
    });
  };

  const setSellerGroupId = (value: string) => {
    const result = {
      target: { value, name: 'sellerGroupId' },
    };
    setPurchaserInfo(result);

    toast.success(<ToastSuccessMessage message={translate('COMPONENTS.SCOREBOARD_MODAL.SELLER_ADDED')} />);
  };

  const isWinner = (points: number, index: number) => {
    const count = winnerCount ?? 3;
    return index < count || data?.scoreboard?.slice(0, count).map((r) => r?.points).includes(points);
  };

  const others = translate('COMMON.OTHERS');
  const splitPerTag = scoreboard?.reduce<Record<string, ScoreboardQuery['scoreboard']>>((result, current) => {
    const key = current?.tags?.[0] || others;
    return {
      ...result,
      [key]: [...(result[key] || []), current],
    };
  }, {});

  const othersLast = ([a]: [string, unknown], [b]: [string, unknown]) => {
    if (a === others) return 1;
    if (b === others) return -1;
    if (a > b) return 1;
    if (b > a) return -1;
    return 0;
  };

  const sellerGroups = splitPerTag && Object.entries(splitPerTag).sort(othersLast).map(([tag, list], index, arr) => (
    <React.Fragment key={tag}>
      {arr.length > 1 && <h3 className="h4">{tag}</h3>}
      <ol className="p-0 mb-4">
        {list?.filter(isDefined).map(({
          _id, names, points, link,
        }, i) => (
          <li
            key={_id}
            className={`d-flex justify-content-between align-items-center py-3 ${i ? 'border-bottom border-dark' : ''}`}
            style={{
              ...(isWinner(points || 0, i) ? { backgroundColor: 'rgba(var(--bs-success-rgb), .3)' } : {}),
              margin: '0 -1.8rem',
              padding: '1.8rem',
              listStyle: 'none',
            }}
          >
            <strong className="me-2">
              {i + 1}
              .
            </strong>
            <strong className="flex-grow-1">
              {names}
              {isWinner(points || 0, i) ? ' 🏆' : ''}
            </strong>
            {translate('COMPONENTS.SCOREBOARD_MODAL.POINTS', {
              count: points || 0,
            })}
            {' '}
            <div className="mx-2"><FacebookShareButton link={link || ''} short /></div>
            <BasketIcon className="scoreboard-basket-icon ms-2" onClick={() => setSellerGroupId(_id)} />
          </li>
        ))}
      </ol>
    </React.Fragment>
  ));

  return (
    <SwipeModal
      isOpen={isOpen}
      className={modalStyle}
      toggle={toggleOpen}
      closeModal={toggleOpen}
    >
      <ModalHeader toggle={toggleOpen} className="scoreboard-header">
        <div className="d-flex align-items-center">
          <TrophyIcon className="text-primary me-2" />
          {translate('COMPONENTS.SCOREBOARD_MODAL.MODAL_TITLE')}
        </div>
      </ModalHeader>
      <ModalBody className="mb-2">
        <div className="px-3 py-4 scoreboard-body">
          {scoreboardMessage
            && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(scoreboardMessage) }} />}
          {isScoreboardEmpty ? (
            <p className="text-center">
              <Trans i18nKey="COMPONENTS.SCOREBOARD_MODAL.EMPTY_SCOREBOARD">
                The scoreboard is still empty.
                <br />
                <Button
                  color="link"
                  className="p-0 align-baseline fw-normal border-0"
                  onClick={toggleOpen}
                >
                  Place an order
                </Button>
                {' '}
                to see the first name here!
              </Trans>
            </p>
          ) : (
            <>
              <p className="mb-4">
                <Trans i18nKey="COMPONENTS.SCOREBOARD_MODAL.BODY_INFO">
                  The list is ordered by most points.
                  <br />
                  Each €3 purchase tranche = 1 point
                </Trans>
              </p>
              {sellerGroups}
            </>
          )}
        </div>
      </ModalBody>
    </SwipeModal>
  );
};

export default ScoreboardModal;
